// src/app/core/debug-telemetry.service.ts
import { Injectable } from '@angular/core';
import { Contract, Interface } from 'ethers';

// ✅ ABI minimal inline pour éviter TS2307 (chemins d'ABI)
const ERC20_MIN_ABI = [
  // balanceOf(address) -> uint256
  'function balanceOf(address) view returns (uint256)',
  // allowance(address,address) -> uint256
  'function allowance(address,address) view returns (uint256)',
  // decimals() -> uint8
  'function decimals() view returns (uint8)',
];

@Injectable({ providedIn: 'root' })
export class DebugTelemetryService {
  // ──────────────────────────────────────────
  // Helpers internes
  // ──────────────────────────────────────────
  private short(u: unknown, max = 10): string {
    const s = String(u ?? '');
    return s.length > max ? s.slice(0, max) + '…' : s;
  }

  private to0x(v: any): string {
    try {
      return v == null
        ? '0x0'
        : (String(v).startsWith('0x') ? String(v) : '0x' + BigInt(v).toString(16));
    } catch {
      return '0x0';
    }
  }

  private hexSlice(data?: string | null, n = 10): string {
    if (!data) return '';
    const s = String(data);
    return s.startsWith('0x') ? s.slice(0, n) : '0x' + s.slice(0, n - 2);
  }

  /** Essaie d’extraire une revert reason standard `Error(string)` (0x08c379a0). */
  private tryDecodeRevert(data?: string): string | null {
    try {
      if (!data || typeof data !== 'string') return null;
      if (!data.startsWith('0x08c379a0')) return null; // selector Error(string)
      const iface = new Interface(['function Error(string)']);
      const [reason] = iface.decodeErrorResult('Error', data);
      return String(reason);
    } catch {
      return null;
    }
  }

  // ──────────────────────────────────────────
  // API publique : logs + lectures on-chain
  // ──────────────────────────────────────────

  /** Snapshot de la route/tx pour debug (aucun effet de bord). */
  logRouteSnapshot(
    addLog: (m: string) => void,
    routeObj: any,
    tx: any,
    key: string
  ): void {
    try {
      const proto =
        routeObj?.providerName ||
        routeObj?.tool ||
        routeObj?.provider ||
        routeObj?.name ||
        'unknown';

      const spender = routeObj?.allowanceTarget || routeObj?.spender || tx?.to || null;
      const to = tx?.to || null;
      const value = tx?.value ?? 0;
      const data = tx?.data ?? '0x';
      const selector = this.hexSlice(data, 10);
      const dataLen = (String(data || '').length / 2) | 0;

      addLog(
        `[sim][route] key=${key} proto=${proto} ` +
        `spender=${spender} to=${to} value=${this.to0x(value)} ` +
        `dataLen=${dataLen} selector=${selector}`
      );
    } catch (e: any) {
      addLog(`[sim][route] snapshot-failed ${e?.message || e}`);
    }
  }

  /** Probe allowance/balance/decimals (lecture seule). */
  async logAllowanceProbe(
    provider: any,
    addLog: (m: string) => void,
    tokenAddr: string | null,
    owner: string | null,
    spender: string | null,
    requiredRaw?: bigint | string
  ): Promise<void> {
    try {
      if (!provider || !tokenAddr || !owner || !spender) return;

      // ⚠️ Contract typé minimalement; on utilise l’accès index-signature pour TS5 (TS4111)
      const erc = new Contract(tokenAddr, ERC20_MIN_ABI, provider);

      const [dec, bal, alw] = await Promise.all([
        erc['decimals']().catch(() => 18),
        erc['balanceOf'](owner).catch(() => 0n),
        erc['allowance'](owner, spender).catch(() => 0n),
      ]);

      const req =
        typeof requiredRaw === 'string' ? BigInt(requiredRaw) : (requiredRaw ?? 0n);

      addLog(
        `[erc20] token=${tokenAddr} decimals=${dec} ` +
        `balance=${bal} allowance=${alw} required=${req}`
      );
    } catch (e: any) {
      addLog(`[erc20] allowance-probe failed ${e?.message || e}`);
    }
  }

  /**
   * Double probe read-only : eth_estimateGas puis (si échec) eth_call.
   * N’altère pas le tx (aucune injection de gas).
   */
  async probeSimulation(
    provider: any,
    addLog: (m: string) => void,
    tx: any
  ): Promise<void> {
    if (!provider || !tx) return;

    const txProbe = { ...tx };

    try {
      const gasHex = await provider.send?.('eth_estimateGas', [txProbe]);
      addLog(`[sim][probe][estimateGas] ok gas=${gasHex}`);
    } catch (e: any) {
      const code = e?.code ?? e?.error?.code;
      const data = e?.data ?? e?.error?.data;
      const reason = this.tryDecodeRevert(typeof data === 'string' ? data : data?.data);

      addLog(
        `[sim][probe][estimateGas] fail code=${code} reason=${reason || '(n/a)'} ` +
        `dataHead=${this.hexSlice((typeof data === 'string' ? data : data?.data) || '', 18)}`
      );

      try {
        const call = await provider.send?.('eth_call', [txProbe, 'latest']);
        addLog(`[sim][probe][eth_call] ok statusRaw=${this.short(call, 42)}`);
      } catch (e2: any) {
        const d2 = e2?.data ?? e2?.error?.data;
        const r2 = this.tryDecodeRevert(typeof d2 === 'string' ? d2 : d2?.data);

        addLog(
          `[sim][probe][eth_call] fail code=${e2?.code ?? e2?.error?.code} ` +
          `reason=${r2 || '(n/a)'} dataHead=${this.hexSlice((typeof d2 === 'string' ? d2 : d2?.data) || '', 18)}`
        );
      }
    }
  }
}
